import { useEffect, useState } from 'react'

import { downloadCSV, formatMoney } from 'common'
import {
  mapExpenseStatusWithColor,
  mapExpenseStatusWithIcon,
} from 'employeeDashboard/common/reimbursementRequests/ExpenseStatusMapping'

import {
  GetAllReimbursementRequest,
  GetAllReimbursementsAsCsvRequest,
  PagedReimbursementRequests,
  ProductIds,
  ReimbursementRequest,
  ReimbursementRequestType,
  getAllCompanyReimbursementRequests,
  getAllCompanyReimbursementRequestsAsCsv,
} from 'core'
import { ExpenseStatus } from 'core/domain/expenses/expenseStatus'

import {
  Button,
  Col,
  Icon,
  RangePickerV2,
  Row,
  SimpleSearch,
  SimpleSelect,
  Table,
  TableColumns,
  Tag,
} from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useLoginUserV2, useQueryRequestV2 } from 'common/hooks'

import './AllReimbursements.less'

const statusLabelMapping = new Map<ExpenseStatus, [string, string]>([
  [ExpenseStatus.AwaitingReview, ['Awaiting Review', 'blue']],
  [ExpenseStatus.Verified, ['Verified', 'green']],
  [ExpenseStatus.ActionRequired, ['Action Required', 'yellow']],
  [ExpenseStatus.Ineligible, ['Ineligible', 'red']],
  [ExpenseStatus.AwaitingAdminReview, ['Awaiting Admin Review', '']],
])

const renderExpenseStatus = (_: string, { expenseStatus }: ReimbursementRequest) => {
  const [label, tagColor] = statusLabelMapping.get(expenseStatus)
  const icon = mapExpenseStatusWithIcon.get(expenseStatus)
  const iconColor = mapExpenseStatusWithColor.get(expenseStatus)
  return (
    <Tag color={tagColor} icon={<Icon fontAwesome type={icon} className={`${iconColor} mr-4`} />}>
      {label}
    </Tag>
  )
}

const columns: TableColumns = [
  {
    title: 'First name',
    dataIndex: 'firstName',
    key: 'firstName',
    sorter: true,
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Last name',
    dataIndex: 'lastName',
    key: 'lastName',
    sorter: true,
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Expense number',
    dataIndex: 'expenseNumber',
    key: 'expenseNumber',
    align: 'right',
    sorter: true,
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Status',
    dataIndex: 'expenseStatus',
    key: 'expenseStatus',
    sorter: true,
    sortDirections: ['descend', 'ascend'],
    render: renderExpenseStatus,
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    sorter: true,
    sortDirections: ['descend', 'ascend'],
    render: (_, { amount }: ReimbursementRequest) => formatMoney(amount, true),
  },
  {
    title: 'Submission date',
    dataIndex: 'submissionDate',
    key: 'submissionDate',
    sorter: true,
    sortDirections: ['descend', 'ascend'],
    render: (_, { submissionDate }: ReimbursementRequest) => parseZoneFormat(submissionDate),
  },
]

interface Props {
  planPeriodId: string
}

const AllReimbursements = ({ planPeriodId }: Props) => {
  const initialValues: GetAllReimbursementRequest = {
    reimbursementType: ReimbursementRequestType.Expense,
    planPeriodId,
    q: '',
    fromDate: null,
    toDate: null,
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'ExpenseNumber',
    orderDescending: true,
  }

  const [filter, setFilter] = useState<GetAllReimbursementRequest>(initialValues)
  const {
    user: { productId },
  } = useLoginUserV2()

  const {
    data: reimbursmentRequests,
    sendQuery: sendGetReimbursementsRequestsQuery,
    isLoading: reimbursementRequestsQueryisLoading,
  } = useQueryRequestV2<PagedReimbursementRequests>()
  const { sendQuery: sendGetAllReimbursmentsAsCsv, isLoading: csvDownloadIsLoading } = useQueryRequestV2<string>()

  useEffect(() => {
    ;(async () =>
      await sendGetReimbursementsRequestsQuery(() => getAllCompanyReimbursementRequests({ ...filter, planPeriodId })))()
  }, [filter, planPeriodId])

  const handleDownloadCsv = async (request: GetAllReimbursementsAsCsvRequest) => {
    const csvString = await sendGetAllReimbursmentsAsCsv(() => getAllCompanyReimbursementRequestsAsCsv(request))
    const fileName =
      `${request.fromDate && request.toDate ? '' : 'All_'}Reimbursement_Requests` +
      (request.fromDate && request.toDate ? `__${request.fromDate}-${request.toDate}` : '') +
      '.csv'

    downloadCSV(csvString, fileName)
  }

  return (
    <>
      <Row className="mb-8">
        <Col span={5}>
          <RangePickerV2
            allowClear
            format="MM/DD/YYYY"
            onChange={(_, range) => {
              const [fromDate, toDate] = range
              setFilter({ ...filter, fromDate, toDate })
            }}
          />
        </Col>

        <Col span={7}>
          {(productId === ProductIds.QsehraId || productId === ProductIds.IchraId) && (
            <SimpleSelect
              className="selector h-100 w-50 ml-8"
              items={[
                { id: ReimbursementRequestType.Expense, name: 'Expenses' },
                { id: ReimbursementRequestType.Premium, name: 'Premiums' },
              ]}
              value={filter.reimbursementType}
              onChange={(value) => setFilter({ ...filter, reimbursementType: value })}
            />
          )}
        </Col>

        <Col span={7}>
          <SimpleSearch onSearch={(values) => setFilter({ ...filter, q: values })} />
        </Col>

        <Col span={5} className="flex flex_justify_end">
          <Button
            type="primary"
            icon="far fa-download"
            iconPosition="left"
            htmlType="button"
            loading={csvDownloadIsLoading}
            disabled={reimbursmentRequests?.items === undefined || reimbursmentRequests?.items.length === 0}
            onClick={() => handleDownloadCsv({ ...filter })}
          >
            Download CSV
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        loading={reimbursementRequestsQueryisLoading}
        dataSource={reimbursmentRequests?.items}
        pagination={{
          total: reimbursmentRequests?.totalItemCount,
          pageSize: reimbursmentRequests?.pageSize,
          current: reimbursmentRequests?.pageNumber,
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '20'],
        }}
        onChange={(pagination, _, sorter) => {
          setFilter({
            ...filter,
            pageNumber: pagination.current,
            pageSize: pagination.pageSize,
            orderColumn: sorter.field || initialValues.orderColumn,
            orderDescending: sorter.order !== 'ascend',
          })
        }}
      />
    </>
  )
}

export default AllReimbursements
